import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { resetAll } from "./baseSlice";

const customIntl = intl();

const initialState = {
  user: null,
  requesting: false,
  error: null,
  action: null,
  dashboard: null,
  isRoleSwitching: false,
  learningPrograms: [],
  compliancePassport: [],
  program: null,
  notifications: [],
  myteam: {
    learningObjects: {
      query: {
        pageNo: 1,
        pageSize: 50,
        sortBy: "asc",
        sortColumn: "firstName",
        namePattern: "",
      },
      countSummary: [],
      itemSummary: [],
      list: [],
    },
    learningPrograms: {
      query: {
        pageNo: 1,
        pageSize: 50,
        sortBy: "asc",
        sortColumn: "firstName",
        namePattern: "",
      },
      countSummary: [],
      itemSummary: [],
      list: [],
    },
    learningMilestones: {
      query: {
        pageNo: 1,
        pageSize: 50,
        sortBy: "asc",
        sortColumn: "firstName",
        namePattern: "",
      },
      countSummary: [],
      itemSummary: [],
      list: [],
    },
  },
  summary: {
    learningStats: {
      totalTimeSpentInSeconds: 0,
      completedLearningProgramCount: 0,
      pendingLearningProgramCount: 0,
    },
    learningStatusCount: [
      {
        status: "completed",
        count: 0,
      },
      {
        status: "inprogress",
        count: 0,
      },
      {
        status: "notstarted",
        count: 0,
      },
      {
        status: "overdue",
        count: 0,
      },
    ],
  },
  fullProfile: null,
};

const selfSlice = createSlice({
  name: "self",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    userDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "user-details-request";
    },
    userDetailsSuccess: (state, action) => {
      state.user = action.payload.response;
      state.requesting = false;
      state.error = null;
      if (action.payload?.response?.role?.scope) {
        let canSwitchDashboard = false;
        Object.keys(action.payload.response.role.scope).forEach((key) => {
          if (action.payload.response.role.scope[key]) {
            canSwitchDashboard = true;
          }
        });
        state.isRoleSwitching = canSwitchDashboard;
      }
    },
    userDetailsFail: (state, action) => {
      state.user = null;
      state.requesting = false;
      state.error = action.payload;
    },
    setDashboard: (state, action) => {
      state.dashboard = action.payload || "learner";
    },
    learningProgramsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "learning-programs-request";
    },
    learningProgramsSuccess: (state, action) => {
      state.learningPrograms = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    learningProgramsFail: (state, action) => {
      state.leaningPrograms = [];
      state.requesting = false;
      state.error = action.payload;
    },
    compliancePassportRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "compliance-passport-request";
    },
    compliancePassportSuccess: (state, action) => {
      state.compliancePassport = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    compliancePassportFail: (state, action) => {
      state.compliancePassport = [];
      state.requesting = false;
      state.error = action.payload;
    },
    assignedProgramRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "assigned-program-request";
    },
    assignedProgramSuccess: (state, action) => {
      state.program = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    assignedProgramFail: (state, action) => {
      state.program = null;
      state.requesting = false;
      state.error = action.payload;
    },
    markAsCompleteRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "mark-as-complete-request";
    },
    markAsCompleteSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
    },
    markAsCompleteFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    learningStatsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "learning-stats-request";
    },
    learningStatsSuccess: (state, action) => {
      state.summary = {
        ...state.summary,
        learningStats: action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    learningStatsFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    learningStatusCountRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "learning-status-count-request";
    },
    learningStatusCountSuccess: (state, action) => {
      state.summary = {
        ...state.summary,
        learningStatusCount: action.payload.response,
      };
      state.requesting = false;
      state.error = null;
    },
    learningStatusCountFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    changePasswordRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "change-password-request";
    },
    changePasswordSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "change_password_success" })
      );
    },
    changePasswordFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      } else {
        toast.error(customIntl.formatMessage({ id: action.payload.response }));
      }
    },
    notificationsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "notifications-request";
    },
    notificationsSuccess: (state, action) => {
      state.notifications = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    notificationsFail: (state, action) => {
      state.notifications = [];
      state.requesting = false;
      state.error = action.payload;
    },
    learningObjectCountSummaryRequest: (state, action) => {
      state.myteam.learningObjects.countSummary = null;
    },
    learningObjectItemSummaryRequest: (state, action) => {
      state.myteam.learningObjects.itemSummary = null;
    },
    learningObjectCountSummarySuccess: (state, action) => {
      state.myteam.learningObjects.countSummary = action.payload.response;
    },
    learningObjectItemSummarySuccess: (state, action) => {
      state.myteam.learningObjects.itemSummary = action.payload.response;
    },
    learningObjectCountSummaryFail: (state, action) => {
      state.myteam.learningObjects.countSummary = [];
    },
    learningObjectItemSummaryFail: (state, action) => {
      state.myteam.learningObjects.itemSummary = [];
    },
    setLearningObjectQuery: (state, action) => {
      state.myteam.learningObjects.query = action.payload;
    },
    learningObjectUsersRequest: (state, action) => {
      state.myteam.learningObjects.list = null;
    },
    learningObjectUsersSuccess: (state, action) => {
      state.myteam.learningObjects.list = action.payload.response;
    },
    learningObjectUsersFail: (state, action) => {
      state.myteam.learningObjects.list = [];
    },
    learningProgramCountSummaryRequest: (state, action) => {
      state.myteam.learningPrograms.countSummary = null;
    },
    learningProgramItemSummaryRequest: (state, action) => {
      state.myteam.learningPrograms.itemSummary = null;
    },
    learningProgramCountSummarySuccess: (state, action) => {
      state.myteam.learningPrograms.countSummary = action.payload.response;
    },
    learningProgramItemSummarySuccess: (state, action) => {
      state.myteam.learningPrograms.itemSummary = action.payload.response;
    },
    learningProgramCountSummaryFail: (state, action) => {
      state.myteam.learningPrograms.countSummary = [];
    },
    learningProgramItemSummaryFail: (state, action) => {
      state.myteam.learningPrograms.itemSummary = [];
    },
    setLearningProgramQuery: (state, action) => {
      state.myteam.learningPrograms.query = action.payload;
    },
    learningProgramUsersRequest: (state, action) => {
      state.myteam.learningPrograms.list = null;
    },
    learningProgramUsersSuccess: (state, action) => {
      state.myteam.learningPrograms.list = action.payload.response;
    },
    learningProgramUsersFail: (state, action) => {
      state.myteam.learningPrograms.list = [];
    },
    learningMilestoneCountSummaryRequest: (state, action) => {
      state.myteam.learningMilestones.countSummary = null;
    },
    learningMilestoneItemSummaryRequest: (state, action) => {
      state.myteam.learningMilestones.itemSummary = null;
    },
    learningMilestoneCountSummarySuccess: (state, action) => {
      state.myteam.learningMilestones.countSummary = action.payload.response;
    },
    learningMilestoneItemSummarySuccess: (state, action) => {
      state.myteam.learningMilestones.itemSummary = action.payload.response;
    },
    learningMilestoneCountSummaryFail: (state, action) => {
      state.myteam.learningMilestones.countSummary = [];
    },
    learningMilestoneItemSummaryFail: (state, action) => {
      state.myteam.learningMilestones.itemSummary = [];
    },
    setLearningMilestoneQuery: (state, action) => {
      state.myteam.learningMilestones.query = action.payload;
    },
    learningMilestoneUsersRequest: (state, action) => {
      state.myteam.learningMilestones.list = null;
    },
    learningMilestoneUsersSuccess: (state, action) => {
      state.myteam.learningMilestones.list = action.payload.response;
    },
    learningMilestoneUsersFail: (state, action) => {
      state.myteam.learningMilestones.list = [];
    },
    learningObjectUserExcelRequest: (state, action) => {},
    learningObjectUserExcelSuccess: (state, action) => {},
    learningObjectUserExcelFail: (state, action) => {},
    learningMilestoneUserExcelRequest: (state, action) => {},
    learningMilestoneUserExcelSuccess: (state, action) => {},
    learningMilestoneUserExcelFail: (state, action) => {},
    learningProgramUserExcelRequest: (state, action) => {},
    learningProgramUserExcelSuccess: (state, action) => {},
    learningProgramUserExcelFail: (state, action) => {},
    fullProfileRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "full-profile-request";
    },
    fullProfileSuccess: (state, action) => {
      state.fullProfile = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    fullProfileFail: (state, action) => {
      state.fullProfile = null;
      state.requesting = false;
      state.error = action.payload;
    },
    updateDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update-profile";
    },
    updateDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "profile" }),
          }
        )
      );
    },
    updateDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateAccountPreferencesRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update-account-preferences";
    },
    updateAccountPreferencesSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "account_preferences" }),
          }
        )
      );
    },
    updateAccountPreferencesFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateNotificationReadRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update-notification-read";
    },
    updateNotificationReadSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "mark_as_read_successfully" })
      );
    },
    updateNotificationReadFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    setPasswordRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "set-password-request";
    },
    setPasswordSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage({ id: "set_password_successfully" })
      );
    },
    setPasswordFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      } else {
        toast.error(customIntl.formatMessage({ id: action.payload.response }));
      }
    },
  },
});

export const {
  assignedProgramFail,
  assignedProgramRequest,
  assignedProgramSuccess,
  compliancePassportFail,
  compliancePassportRequest,
  compliancePassportSuccess,
  learningProgramsFail,
  learningProgramsRequest,
  learningProgramsSuccess,
  learningStatsFail,
  learningStatsRequest,
  learningStatsSuccess,
  learningStatusCountFail,
  learningStatusCountRequest,
  learningStatusCountSuccess,
  markAsCompleteFail,
  markAsCompleteRequest,
  markAsCompleteSuccess,
  setDashboard,
  userDetailsFail,
  userDetailsRequest,
  userDetailsSuccess,
  changePasswordFail,
  changePasswordRequest,
  changePasswordSuccess,
  notificationsFail,
  notificationsRequest,
  notificationsSuccess,
  learningMilestoneCountSummaryFail,
  learningMilestoneCountSummaryRequest,
  learningMilestoneCountSummarySuccess,
  learningMilestoneItemSummaryFail,
  learningMilestoneItemSummaryRequest,
  learningMilestoneItemSummarySuccess,
  learningMilestoneUsersFail,
  learningMilestoneUsersRequest,
  learningMilestoneUsersSuccess,
  learningObjectCountSummaryFail,
  learningObjectCountSummaryRequest,
  learningObjectCountSummarySuccess,
  learningObjectItemSummaryFail,
  learningObjectItemSummaryRequest,
  learningObjectItemSummarySuccess,
  learningObjectUsersFail,
  learningObjectUsersRequest,
  learningObjectUsersSuccess,
  learningProgramCountSummaryFail,
  learningProgramCountSummaryRequest,
  learningProgramCountSummarySuccess,
  learningProgramItemSummaryFail,
  learningProgramItemSummaryRequest,
  learningProgramItemSummarySuccess,
  learningProgramUsersFail,
  learningProgramUsersRequest,
  learningProgramUsersSuccess,
  setLearningMilestoneQuery,
  setLearningObjectQuery,
  setLearningProgramQuery,
  learningObjectUserExcelFail,
  learningObjectUserExcelRequest,
  learningObjectUserExcelSuccess,
  learningMilestoneUserExcelFail,
  learningMilestoneUserExcelRequest,
  learningMilestoneUserExcelSuccess,
  learningProgramUserExcelFail,
  learningProgramUserExcelRequest,
  learningProgramUserExcelSuccess,
  fullProfileFail,
  fullProfileRequest,
  fullProfileSuccess,
  updateDetailFail,
  updateDetailRequest,
  updateDetailSuccess,
  updateAccountPreferencesFail,
  updateAccountPreferencesRequest,
  updateAccountPreferencesSuccess,
  updateNotificationReadRequest,
  updateNotificationReadSuccess,
  updateNotificationReadFail,
  setPasswordFail,
  setPasswordRequest,
  setPasswordSuccess,
} = selfSlice.actions;
export default selfSlice.reducer;
